import { ApolloError, useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  SET_CHECKOUT_RULE_SESSION,
  type CheckoutRuleOption,
  type CheckoutRuleType,
  type ISetCheckoutRule,
  type ISetCheckoutRuleVariables,
} from '@commerce/core'

/**
 * `not-offered` is the server refusing the value against its *freshly
 * recomputed* list — either the option genuinely expired between read and
 * write (a cutoff passed, midnight rolled over) or the value was reformatted
 * on the way back. The recovery is re-fetch and re-prompt, never a retry of
 * the same value. `unreachable` is the network failing to carry the question.
 */
export type SetCheckoutRuleFailure = 'not-offered' | 'unreachable'

export class SetCheckoutRuleError extends Error {
  constructor(public readonly reason: SetCheckoutRuleFailure) {
    super(`setCheckoutRuleSession failed: ${reason}`)
  }
}

export interface UseSetCheckoutRule {
  /**
   * Persists the choice onto the session. `value` is the offered option
   * **object**, passed through untouched — see `ISetCheckoutRuleVariables`.
   */
  setRule: (ruleType: CheckoutRuleType, value: CheckoutRuleOption) => Promise<void>
  loading: boolean
}

export const useSetCheckoutRule = (): UseSetCheckoutRule => {
  const { slug = '', sessionId = '' } = useParams()
  const [mutate, { loading }] = useMutation<ISetCheckoutRule, ISetCheckoutRuleVariables>(
    SET_CHECKOUT_RULE_SESSION,
  )

  const setRule = useCallback(
    async (ruleType: CheckoutRuleType, value: CheckoutRuleOption): Promise<void> => {
      try {
        await mutate({
          variables: { storefrontName: slug, sessionUid: sessionId, ruleType, value },
        })
      } catch (error) {
        if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
          throw new SetCheckoutRuleError('not-offered')
        }
        throw new SetCheckoutRuleError('unreachable')
      }
    },
    [mutate, slug, sessionId],
  )

  return { setRule, loading }
}
